/**
 * Cruisers — rare capital-ship raiders (their own big dark/red hull sprite).
 *
 * They only appear from the 4th drive jump onward, never more than 2 per planet
 * layer, and are SLOW (about 20% faster than the player's Mk IV Battleship).
 *
 * Fighting style — the BROADSIDE:
 *   • Aggro → hold a standoff distance and turn side-on to the player, orbiting so
 *     one flank stays presented. Its 2–4 side turrets rotate to track the player
 *     but only within a side arc, so — exactly like the player's turrets — they can
 *     only bear on the ship's flanks, bombarding the player as it sails past.
 *   • Not aggro → drift slowly toward a roam waypoint.
 *
 * A Cruiser satisfies the turret/cannon `Damageable` shape, so the player's
 * turrets, cannons, and lock-on target it directly.
 */
import { Vector2 } from "@/math/Vector2";
import { CRUISER } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";
import type { EnemyBullet } from "./EnemyShips";

export interface CruiserTurret {
  angle: number; // world aim angle
  cooldown: number;
  flash: number; // muzzle flash 0..1
}

export interface Cruiser {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  turretCount: number; // 2..4 live turrets
  turrets: CruiserTurret[]; // always length CRUISER.mounts.length
  aggro: boolean;
  broadside: 1 | -1; // which flank it keeps presented to the player
  roam: Vector2;
  dead: boolean; // death handled (XP/explosion awarded once)
  backupState: number; // 0 = unused, 1 = broadcasting radio, 2 = spent
  backupTimer: number; // radio-broadcast countdown (also drives the VFX phase)
  launchStraight: number; // >0 → just launched from a station hangar, flying straight out
}

export interface CruiserCtx {
  playerX: number;
  playerY: number;
  playerRadius: number;
  playerAlive: boolean;
  playerSpeed: number; // the player's current max speed (cruise = 20% above this)
  warp: number; // drive-jump count — call-for-backup unlocks from CRUISER.backupFromWarp
  bodies: MineableBody[];
  onHitPlayer: (dmg: number, x: number, y: number) => void;
  onDeath: (x: number, y: number) => void;
  onCallBackup: (x: number, y: number) => void; // summon the jump-in squad
}

function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function randomWaypoint(): Vector2 {
  const a = Math.random() * Math.PI * 2;
  const d = 300 + Math.random() * 1400;
  return new Vector2(Math.cos(a) * d, Math.sin(a) * d);
}

export class CruiserManager {
  readonly ships: Cruiser[] = [];
  readonly bullets: EnemyBullet[] = [];
  private readonly spawn: { x: number; y: number };

  constructor(count: number, spawn: { x: number; y: number } = { x: 0, y: 0 }) {
    this.spawn = { x: spawn.x, y: spawn.y };
    for (let i = 0; i < count; i++) this.ships.push(this.make());
  }

  private spawnPos(): Vector2 {
    for (let tries = 0; tries < 12; tries++) {
      const a = Math.random() * Math.PI * 2;
      const d = 900 + Math.random() * 700;
      const x = Math.cos(a) * d;
      const y = Math.sin(a) * d;
      const dx = x - this.spawn.x;
      const dy = y - this.spawn.y;
      if (dx * dx + dy * dy >= CRUISER.minSpawnDist * CRUISER.minSpawnDist) {
        return new Vector2(x, y);
      }
    }
    return new Vector2(-this.spawn.x, -this.spawn.y - 1000);
  }

  private make(): Cruiser {
    const pos = this.spawnPos();
    const heading = Math.atan2(-pos.y, -pos.x);
    const n = CRUISER.mounts.length;
    const turretCount =
      CRUISER.turretMin +
      Math.floor(Math.random() * (CRUISER.turretMax - CRUISER.turretMin + 1));
    const turrets: CruiserTurret[] = [];
    for (let i = 0; i < n; i++) {
      turrets.push({ angle: heading, cooldown: Math.random() * CRUISER.fireInterval, flash: 0 });
    }
    return {
      pos,
      vel: new Vector2(),
      heading,
      hp: CRUISER.maxHp,
      maxHp: CRUISER.maxHp,
      shield: CRUISER.maxShield,
      maxShield: CRUISER.maxShield,
      shieldFlash: 0,
      hitTimer: 999,
      turretCount: Math.min(turretCount, n),
      turrets,
      aggro: false,
      broadside: Math.random() < 0.5 ? 1 : -1,
      roam: randomWaypoint(),
      dead: false,
      backupState: 0,
      backupTimer: 0,
      launchStraight: 0,
    };
  }

  /** Spawn one cruiser at a point, flying straight out along `heading` for a beat before it
   * engages (ring-station hangar launch). */
  spawnAt(pos: { x: number; y: number }, heading: number): void {
    const c = this.make();
    c.pos.set(pos.x, pos.y);
    c.heading = heading;
    c.launchStraight = 1.0; // fly straight ~1s, matching the launched fighters
    for (const t of c.turrets) t.angle = heading;
    this.ships.push(c);
  }

  update(dt: number, ctx: CruiserCtx): void {
    for (const s of this.ships) this.updateShip(s, dt, ctx);

    // Advance red bolts; a strike on the player deals damage.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      if (ctx.playerAlive) {
        const rr = ctx.playerRadius + 5;
        if ((ctx.playerX - b.x) ** 2 + (ctx.playerY - b.y) ** 2 < rr * rr) {
          ctx.onHitPlayer(CRUISER.boltDamage, b.x, b.y);
          hit = true;
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }
  }

  private updateShip(s: Cruiser, dt: number, ctx: CruiserCtx): void {
    if (s.hp <= 0) {
      if (!s.dead) {
        ctx.onDeath(s.pos.x, s.pos.y);
        s.dead = true;
      }
      return; // capital ships do not respawn
    }

    // Just launched from a station hangar → drift straight out for a beat before engaging.
    if (s.launchStraight > 0) {
      s.launchStraight -= dt;
      const spd = ctx.playerSpeed || 200;
      s.vel.set(Math.cos(s.heading) * spd, Math.sin(s.heading) * spd);
      s.pos.x += s.vel.x * dt;
      s.pos.y += s.vel.y * dt;
      s.hitTimer += dt;
      s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
      for (const t of s.turrets) t.angle = s.heading;
      return;
    }

    s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
    s.hitTimer += dt;
    for (const t of s.turrets) {
      t.flash = Math.max(0, t.flash - dt * 5);
      t.cooldown -= dt;
    }

    const dx = ctx.playerX - s.pos.x;
    const dy = ctx.playerY - s.pos.y;
    const dist = Math.hypot(dx, dy) || 1e-3;
    const toPlayer = Math.atan2(dy, dx);
    if (dist < CRUISER.aggroRange) s.aggro = true;
    else if (dist > CRUISER.aggroRange * 1.6) s.aggro = false;

    // Call for backup — ONCE per life, from warp 6, once the player has drawn some
    // blood: broadcast a radio wave, then jump-in a fighter squadron.
    if (
      s.backupState === 0 &&
      s.aggro &&
      ctx.playerAlive &&
      ctx.warp >= CRUISER.backupFromWarp &&
      s.hp < s.maxHp * 0.85
    ) {
      s.backupState = 1;
      s.backupTimer = CRUISER.backupRadioTime;
    }
    if (s.backupState === 1) {
      s.backupTimer -= dt;
      if (s.backupTimer <= 0) {
        ctx.onCallBackup(s.pos.x, s.pos.y);
        s.backupState = 2;
      }
    }

    let desired: number;
    let speed: number;

    if (s.aggro && ctx.playerAlive) {
      // Broadside orbit: fly tangent to the player, holding the standoff distance,
      // keeping one flank presented.
      const cruise = clamp(ctx.playerSpeed * CRUISER.speedMul, CRUISER.speedMin, CRUISER.speedMax);
      const tangent = toPlayer + s.broadside * (Math.PI / 2);
      const radialErr = dist - CRUISER.standoff; // >0 too far, <0 too close
      const pull = clamp(radialErr / 300, -1, 1);
      const rx = dx / dist;
      const ry = dy / dist;
      const vx = Math.cos(tangent) + rx * pull * 0.9;
      const vy = Math.sin(tangent) + ry * pull * 0.9;
      desired = Math.atan2(vy, vx);
      speed = cruise;
    } else {
      if ((s.roam.x - s.pos.x) ** 2 + (s.roam.y - s.pos.y) ** 2 < 110 * 110) {
        s.roam = randomWaypoint();
      }
      desired = Math.atan2(s.roam.y - s.pos.y, s.roam.x - s.pos.x);
      speed = CRUISER.patrolSpeed;
    }

    desired = this.avoidRocks(s, ctx.bodies, desired);
    this.steer(s, dt, desired, speed);
    this.updateTurrets(s, dt, ctx, dist);

    if (s.hitTimer >= CRUISER.regenDelay && s.shield < s.maxShield) {
      s.shield = Math.min(s.maxShield, s.shield + CRUISER.shieldRegenPerSec * dt);
    }
  }

  /** Rotate each side turret to track the player, but only within its flank arc;
   * fire when lined up and the player is on that side. */
  private updateTurrets(s: Cruiser, dt: number, ctx: CruiserCtx, dist: number): void {
    const h = s.heading;
    // Mount transform matches the hull sprite render (rotation = heading − π/2), so each
    // turret sits on its painted gun; hs = worldHeight / textureHeight.
    const ct = Math.sin(h); // cos(h − π/2)
    const st = -Math.cos(h); // sin(h − π/2)
    const hs = CRUISER.spriteWorldHeight / CRUISER.spriteTexHeight;
    const canFire = s.aggro && ctx.playerAlive && dist < CRUISER.turretRange;
    for (let i = 0; i < s.turretCount; i++) {
      const t = s.turrets[i]!;
      const off = CRUISER.mounts[i]!;
      const mx = s.pos.x + hs * (off.ox * ct - off.oy * st);
      const my = s.pos.y + hs * (off.ox * st + off.oy * ct);
      // Like the player: each mount only covers its own 180° side — hold fire otherwise.
      const aim = Math.atan2(ctx.playerY - my, ctx.playerX - mx);
      const arcSign = off.ox < 0 ? 1 : -1;
      const onSide = arcSign * norm(aim - h) >= -0.03;
      const d = norm(aim - t.angle);
      const step = CRUISER.turretTurn * dt;
      t.angle += d > step ? step : d < -step ? -step : d;
      if (canFire && onSide && t.cooldown <= 0 && Math.abs(norm(aim - t.angle)) < CRUISER.fireCone) {
        const ax = Math.cos(t.angle);
        const ay = Math.sin(t.angle);
        const bx = -ay; // perpendicular → twin bolts fly parallel
        const by = ax;
        for (let k = 0; k < CRUISER.boltsPerShot; k++) {
          const off2 = (k * 2 - (CRUISER.boltsPerShot - 1)) * CRUISER.barrelSpread;
          this.bullets.push({
            x: mx + ax * CRUISER.muzzleLen + bx * off2,
            y: my + ay * CRUISER.muzzleLen + by * off2,
            vx: ax * CRUISER.boltSpeed,
            vy: ay * CRUISER.boltSpeed,
            life: CRUISER.boltLife,
          });
        }
        t.cooldown = CRUISER.fireInterval;
        t.flash = 1;
      }
    }
  }

  private avoidRocks(s: Cruiser, bodies: MineableBody[], desired: number): number {
    let ax = 0;
    let ay = 0;
    for (const b of bodies) {
      if (b.ore <= 0) continue;
      const dx = s.pos.x - b.pos.x;
      const dy = s.pos.y - b.pos.y;
      const rr = b.radius + CRUISER.collisionRadius + CRUISER.avoidRange;
      const d2 = dx * dx + dy * dy;
      if (d2 < rr * rr && d2 > 1) {
        const d = Math.sqrt(d2);
        const w = (rr - d) / rr;
        ax += (dx / d) * w;
        ay += (dy / d) * w;
      }
    }
    if (ax === 0 && ay === 0) return desired;
    const dvx = Math.cos(desired) + ax * 1.8;
    const dvy = Math.sin(desired) + ay * 1.8;
    return Math.atan2(dvy, dvx);
  }

  private steer(s: Cruiser, dt: number, desired: number, speed: number): void {
    const diff = norm(desired - s.heading);
    const maxTurn = CRUISER.turnRate * dt;
    s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
    const vx = Math.cos(s.heading) * speed;
    const vy = Math.sin(s.heading) * speed;
    s.vel.set(vx, vy);
    s.pos.x += vx * dt;
    s.pos.y += vy * dt;
  }
}
